<script setup lang="ts">
import { Frequency, type VacancyType as VacancyTypeType, SearchViewMode } from '#imports'
import type { LocationQueryValue } from '#vue-router'

const { overviewType } = defineProps<{
  overviewType: VacancyTypeType
}>()

const { t } = useI18n({
  useScope: 'local',
})

const { categories } = await useCategories()
// Passing no `vacancyType` to useActivityTypes() will return all activity types without a vacancyType
const { hierarchicalActivityTypes } = await useActivityTypes()
// Passing no `vacancyType` to useAudiences() will return all audiences without a vacancyType
const { audiences } = await useAudiences()

const frequencies: NamedValueItem[] = Object.values(Frequency)
  .filter(freq => freq != Frequency.MultipleDays)
  .map(freq => ({
    value: freq,
    name: t(`frequency.${freq}`),
  }))

const router = useRouter()
const route = useRoute()

const activeViewMode = useState<SearchViewMode>('searchMapViewMode', () => SearchViewMode.list)

const showSidebar = computed(() => true) // activeViewMode.value === SearchViewMode.list)
// We currently have only one CTA, so we can show it when the overviewType is Volunteering
const showCTA = computed(() => overviewType == VacancyType.Volunteering)

// The types
// Not defined globally at this moment, because we might only need it here
enum VacancySubType {
  All = 'all',
  P2p = 'p2p',
}

const search = useState('vacancySearch', () => '')
const selectedFrequencies = useState<string[]>('vacancySelectedFrequencies', () => [])
const selectedActivityTypeIds = useState<id[]>('vacancySelectedActivityTypeIds', () => [])
const selectedCategoryIds = useState<id[]>('vacancySelectedCategoryIds', () => [])
const selectedAudienceIds = useState<id[]>('vacancySelectedAudienceIds', () => [])
const selectedVacancySubType = useState<VacancySubType | null>('vacancySelectedSubType', () => null)

const postcode = useState('vacancyPostcode', () => '')
const distance = useState<number>('vacancyDistance', () => 1)
const isFilteringByLocation = computed(
  // We need at least 6 characters for a valid postcode
  // 4 chars is not supported (yet)
  () => !!postcode.value && postcode.value.length >= 6,
)
const flexibleLocation = useState('vacancyFlexibleLocation', () => false)
const fromHome = useState('vacancyFromHome', () => false)

// For listview, 10 by default, for mapView let's do 250 for now. For Nijmegen we need 1000 to show all,
// so we still have 3 pages this way. For other partners, 250 is probably enough.
const perPage = useState<number>('vacancyPerPage', () => showCTA.value ? 9 : 10)
const perPageForCurrentViewMode = computed(() => activeViewMode.value === SearchViewMode.map ? 250 : perPage.value)
const page = useState('vacancyPage', () => 1)
const sortBy = useState('vacancySortBy', () => 'latest')

function parseArrayQueryParam(
  queryParam: LocationQueryValue | LocationQueryValue[],
): string[] {
  if (Array.isArray(queryParam)) {
    return queryParam.map(String)
  }
  else if (queryParam) {
    return [String(queryParam)]
  }
  return []
}

const searchWordFilter = 'filter[title_or_full_description_or_organization_name_cont]'

// Update filters refs based on the URL query
// TODO: this is messy. We basically only want to overwrite the state for params that are present in the query.
const initializeFiltersFromQuery = () => {
  const urlQuery = route.query

  // The categories can be set while all other filters are not set, when coming from a content block.
  const categories = parseArrayQueryParam(urlQuery['filter[categories_id_in][]'])
  // Only overwrite the state if the categories are present in the query
  // TODO: do this for all other params as well
  if (categories) {
    selectedCategoryIds.value = categories
  }

  if (urlQuery['filter[frequency_in][]']) {
    selectedFrequencies.value = parseArrayQueryParam(
      urlQuery['filter[frequency_in][]'],
    )
  }

  // As a quickfix, just return if there is vacancy_type_eq, assuming all others aren't set either.
  if (!urlQuery['filter[vacancy_type_eq]']) return

  search.value
    = typeof urlQuery[searchWordFilter] === 'string'
      ? urlQuery[searchWordFilter]
      : ''

  selectedVacancySubType.value = urlQuery['filter[vacancy_type_eq]'] as VacancySubType

  // Location section
  postcode.value = urlQuery['filter[postcode]'] as string
  distance.value = parseInt(urlQuery['filter[distance]'] as string) || 1
  // The flexible location filter is only available when the user has entered a postcode,
  // to make it possible to filter vacancies that don't have a location.
  flexibleLocation.value = urlQuery['filter[flexible_location]'] === 'true'
  // The from home filter is always visible, because the user might want to search for something
  // that is always from home or online. For the flexible location filter, that makes a bit less sense,
  // that's why we only show it when the user has entered a postcode.
  fromHome.value = urlQuery['filter[from_home]'] === 'true'

  // Array-based filters

  selectedActivityTypeIds.value = parseArrayQueryParam(
    urlQuery['filter[activity_types_id_in][]'],
  )
  selectedAudienceIds.value = parseArrayQueryParam(
    urlQuery['filter[audiences_id_in][]'],
  )

  const perPageParam = parseInt(urlQuery['page[size]'] as string)
  if (perPageParam > 0) {
    perPage.value = perPageParam
  }
  const pageParam = parseInt(urlQuery['page[number]'] as string)
  if (pageParam > 0) {
    page.value = pageParam
  }
  // TODO: sorting is not implemented yet
  // const sortByParam = urlQuery['sortBy'] as string
  // if (sortByParam) {
  //   sortBy.value = sortByParam
  // }
}

initializeFiltersFromQuery()

const vacancySubTypes = Object.values(VacancySubType)
const showSubTypeFilter = overviewType == VacancyType.Volunteering

const filterOnP2p = computed(() => {
  return (
    (overviewType == VacancyType.Volunteering
    && selectedVacancySubType.value == VacancySubType.P2p)
    || overviewType == VacancyType.P2p
  )
})

// Note: this needs to match the types that the backend actually uses, which are:
// - volunteering
// - activity
// - p2p
const filterByVacancyType = computed<VacancyTypeType | undefined>(() => {
  if (filterOnP2p.value) {
    return VacancyType.P2p
  }
  return overviewType
})

// Debounce the search input
const searchDebounced = refDebounced(search, 150)

const searchOptionsQuery = computed(() => {
  return {
    [searchWordFilter]: searchDebounced.value,
    'filter[vacancy_type_eq]': filterByVacancyType.value,
    'filter[frequency_in][]': selectedFrequencies.value,
    'filter[activity_types_id_in][]': selectedActivityTypeIds.value,
    'filter[categories_id_in][]': selectedCategoryIds.value,
    'filter[audiences_id_in][]': selectedAudienceIds.value,
    'filter[postcode]': isFilteringByLocation.value ? postcode.value : null,
    'filter[distance]': isFilteringByLocation.value ? distance.value : null,
    'filter[flexible_location]': isFilteringByLocation.value
      ? flexibleLocation.value
      : null,
    'filter[from_home]': fromHome.value,
  }
})

const query = computed(() => {
  const rawQuery = {
    ...searchOptionsQuery.value,
    ...{
      'public_search': true,
      'filter[status_eq]': 'published',
      'filter[for_meedoen_eq]': false,
      'page[size]': perPageForCurrentViewMode.value,
      'page[number]': page.value,
    },
  }
  return cleanObject(rawQuery)
})

const { data: vacancies, status, data } = useFetchVacancies(query)

const totalItems = computed(() => vacancies.value?._meta?.page?.totalItems)
const facets = computed(() => vacancies.value?._meta?.facets)

// watch(() => vacancies.value, (vacancies) => {
//   console.log(`vac meta`, vacancies?._meta, vacancies)
// }, { immediate: true })

// watch(totalItems, (totalItems) => {
//   console.log(`totalItems`, totalItems)
//   if (totalItems !== null && totalItems !== undefined) {
//     lastCount.value = totalItems
//   }
// }, { immediate: true })

const noSearchResults = computed(() => totalItems.value === 0)
// If there are any previous search results, we want to show the same amount of placeloaders.
// If there were no previous results, we want to show a full page of placeloaders.
const numberOfPlaceLoaders = computed(() =>
  vacancies.value?.length && vacancies.value?.length > 0 ? vacancies.value.length : perPageForCurrentViewMode.value,
)

// Enable once we have location search working
const sortEnabled = false

// Reset the page when the search options or perPage changes
watch([searchOptionsQuery, perPageForCurrentViewMode], () => {
  page.value = 1
})

// Update the URL query when the filters change
watch(
  query,
  (query) => {
    console.log(`query`, query)
    router.push({ query })
  },
  { deep: true },
)

const pluralTypeName = t(`pluralName.${overviewType}`)

// Map vacancies to items for the map
const mapItems = computed(() => {
  return (vacancies.value || [])?.map(v => ({
    id: v.id,
    title: v.title,
    description: v.fullDescription,
    latitude: v.address?.latitude,
    longitude: v.address?.longitude,
    formattedAddress: v.address?.formattedAddress,
    avatarUrl: v.organization?.logoUrl,
    to: `/vacancies/${v.id}`,
  }))
})

</script>

<template>
  <div class="mb-6">
    <!-- Search bar -->
    <div class="relative">
      <BaseCard
        shape="curved"
        class="ptablet:py-6 ptablet:px-4 ptablet:grid ptablet:grid-cols-12 ltablet:divide-x divide-muted-200 dark:divide-muted-700 mb-10 flex w-full flex-col items-center py-2 sm:flex-row sm:py-0 lg:divide-x"
      >
        <div
          class="ptablet:ps-4 ptablet:col-span-6 w-full py-2 pe-4 ps-4 sm:w-auto sm:grow sm:ps-2"
        >
          <BaseInput
            v-model.trim="search"
            shape="curved"
            icon="lucide:search"
            :placeholder="t('searchInputPlaceholder')"
          />
        </div>
        <div
          v-if="showSubTypeFilter"
          class="ptablet:col-span-6 w-full flex-1 px-4 py-2 sm:w-auto"
        >
          <BaseSelect
            v-model="selectedVacancySubType"
            shape="curved"
            icon="lucide:briefcase"
            label=""
            hide-label
          >
            <option value="">
              Type vacature
            </option>
            <option
              v-for="vacancyType in vacancySubTypes"
              :key="vacancyType"
              :value="vacancyType"
            >
              {{ t(`vacancyType.${vacancyType}`) }}
            </option>
          </BaseSelect>
        </div>
      </BaseCard>
    </div>

    <!-- Grid -->
    <div class="grid grid-cols-12 gap-6">
      <!-- Sidebar column -->
      <div
        v-if="showSidebar"
        class="ptablet:col-span-4 ltablet:col-span-4 col-span-12 lg:col-span-3"
      >
        <div class="w-full">
          <div class="space-y-8">
            <BaseCard class="flex flex-col gap-y-4 px-6 py-5">
              <BaseHeading
                as="h3"
                weight="medium"
                size="md"
                color="theme"
              >
                {{ t('location') }}
              </BaseHeading>

              <VacancySearchPostcodeInput v-model="postcode" />

              <Transition
                enter-active-class="transition duration-500 ease-in"
                enter-from-class="max-h-0 opacity-0"
                enter-to-class="max-h-[150px] opacity-100"
                leave-active-class="transition duration-100 ease-out"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <div
                  v-show="isFilteringByLocation"
                  class="flex flex-col gap-y-4"
                >
                  <BaseSlider
                    v-model="distance"
                    :min="1"
                    :max="25"
                    :format="(value: any) => `${parseInt(value)} km`"
                    :label="t('distance')"
                  />

                  <BaseSwitchThin
                    v-model="flexibleLocation"
                    label="Inclusief flexibele locatie"
                  />
                </div>
              </Transition>

              <BaseSwitchThin v-model="fromHome" :label="t('fromHome')" />
            </BaseCard>

            <VacancySearchSidebarCard
              v-model="selectedFrequencies"
              :title="t('frequency')"
              :items="frequencies"
              :item-counts="facets?.frequencies"
            />

            <VacancySearchSidebarCard
              v-model="selectedCategoryIds"
              :title="t('category')"
              :items="categories"
              :item-counts="facets?.categoryIds"
            />

            <VacancySearchSidebarCard
              v-model="selectedAudienceIds"
              :title="t('audience')"
              :items="audiences"
              :item-counts="facets?.audienceIds"
            />

            <VacancySearchSidebarCard
              v-model="selectedActivityTypeIds"
              :title="t('activityType')"
              :items="hierarchicalActivityTypes"
              :item-counts="facets?.activityTypeIds"
            />
          </div>
        </div>
      </div>

      <!-- Column -->
      <div
        :class="{
          'ptablet:col-span-8 ltablet:col-span-8 col-span-12 lg:col-span-9': showSidebar,
          'col-span-full': !showSidebar,
        }"
        class=""
      >
        <div class="mb-6 mt-0 flex justify-between">
          <!-- Title + count -->
          <div class="mb-6 mt-12 flex w-full items-center justify-between sm:mt-0">
            <div class="">
              <BaseHeading
                as="h2"
                size="lg"
                weight="medium"
                lead="tight"
              >
                <span v-if="status == 'pending'">Aan het zoeken...</span>
                <span v-else-if="status == 'success'">{{
                  t('vacancyCount', { count: totalItems, pluralTypeName })
                }}</span>
                <span v-else>{{ t('somethingWentWrong') }}</span>
              </BaseHeading>
              <BaseParagraph size="sm">
                <span class="text-muted-500">
                  {{ t('vacancyCountText', { pluralTypeName }) }}
                </span>
              </BaseParagraph>
            </div>

            <SearchViewModeToggle v-model="activeViewMode" />
          </div>

          <!-- Sort filter -->
          <div v-if="sortEnabled" class="flex items-center gap-x-2">
            <label for="sortBy" class="nui-label">{{ t('sortByLabel') }}</label>
            <BaseSelect
              id="sortBy"
              v-model="sortBy"
              label=""
              shape="rounded"
              :classes="{
                wrapper: 'w-40',
              }"
            >
              <option value="latest">
                {{ t('sortBy.latest') }}
              </option>
              <option value="distance">
                {{ t('sortBy.distance') }}
              </option>
            </BaseSelect>
          </div>
        </div>
        <!-- Inner grid -->
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SearchMap
            v-if="activeViewMode === SearchViewMode.map"
            :items="mapItems"
          />

          <template v-else-if="status == 'pending'">
            <VacancySearchPlaceLoadCard
              v-for="index in numberOfPlaceLoaders"
              :key="index"
            />
          </template>

          <NoSearchResult v-else-if="noSearchResults" class="col-span-full" />

          <template v-else>
            <VacancySearchCard
              v-for="vacancy in vacancies"
              :key="vacancy.id"
              :vacancy="vacancy"
              class="col-span-1"
            />

            <!-- Still experimenting with wull with or n-th card grid item -->
            <VacancySearchCallToAction v-if="showCTA" class="col-span-1" />
          </template>
        </div>

        <div v-if="!noSearchResults" class="mt-6">
          <BasePagination
            :total-items="totalItems ?? 0"
            :item-per-page="perPageForCurrentViewMode"
            :current-page="page"
            :no-router="true"
            shape="curved"
            @update:current-page="page = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "searchInputPlaceholder": "Search keywords",
    "search": "Search",
    "location": "Location",
    "distance": "Distance",
    "fromHome": "From home or online",
    "pluralName": {
      "volunteering": "vacancies",
      "activity": "activities",
      "p2p": "1-on-1 requests"
    },
    "vacancyCount": "Showing {count} vacancies",
    "vacancyCountText": "These are the matching {pluralTypeName} that we found",
    "somethingWentWrong": "Something went wrong while fetching results",
    "sortByLabel": "Sort by",
    "sortBy": {
      "latest": "Latest",
      "distance": "Distance"
    },
    "frequency": "Frequency",
    "frequency.once": "Once",
    "frequency.flexible": "Flexible",
    "frequency.regularly": "Regularly",
    "category": "Category",
    "audience": "Audience",
    "activityType": "Activity type",
    "vacancyType.all": "All types of vacancies",
    "vacancyType.p2p": "1-on-1 help",
    "viewMode": {
      "list": "Lijst",
      "map": "Kaart"
    }
  },
  "nl": {
    "searchInputPlaceholder": "Zoekwoord",
    "search": "Zoeken",
    "location": "Locatie",
    "distance": "Afstand",
    "fromHome": "Vanuit huis of online",
    "pluralName": {
      "volunteering": "vacatures",
      "activity": "activiteiten",
      "p2p": "1:1 hulpvragen"
    },
    "vacancyCount": "{count} {pluralTypeName} gevonden",
    "vacancyCountText": "Deze {pluralTypeName} matchen bij je zoekopdracht",
    "somethingWentWrong": "Er is iets misgegaan bij het ophalen van de resultaten",
    "sortByLabel": "Sorteer op",
    "sortBy": {
      "latest": "Nieuwste",
      "distance": "Afstand"
    },
    "category": "Wat spreekt je aan?",
    "audience": "Doelgroep",
    "activityType": "Wat wil je doen?",
    "frequency": "Frequentie",
    "frequency.once": "Eenmalig",
    "frequency.flexible": "Flexibel",
    "frequency.regularly": "Regelmatig",
    "vacancyType.all": "Alle typen vacatures",
    "vacancyType.p2p": "Eén op één hulp",
    "viewMode": {
      "list": "Lijst",
      "map": "Kaart"
    }
  }
}
</i18n>
